/**
 * TemplateSelector Component
 *
 * Popover-based template management panel for label layout templates.
 * Includes label type selector, canvas size selector, and template CRUD.
 *
 * Features:
 * - Label type selector (POD, P&P, BOTH) with color-coded badges
 * - Canvas size selector (compact 32x57mm, standard 4x3", shipping 4x6")
 * - Template popover grouped by canvas size
 * - Save/save as dialog with template name and label type
 * - Duplicate, set default, and delete actions on all templates
 * - AlertDialog confirmations (no native confirm())
 * - Dirty indicator with amber dot
 * - Restore defaults button to re-seed built-in templates
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Plus, Trash2, Copy, Star, Filter, Save, Loader2, RotateCcw } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useLabelLayoutStore } from './label-layout-store';
import { CANVAS_PRESETS, type CanvasPresetKey, type LabelType } from './label-layout-types';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const LABEL_TYPES: LabelType[] = ['POD', 'P&P', 'BOTH'];

const LABEL_TYPE_COLORS: Record<LabelType, string> = {
  POD: 'bg-blue-100 text-blue-800',
  'P&P': 'bg-green-100 text-green-800',
  BOTH: 'bg-gray-100 text-gray-800',
};

export function TemplateSelector() {
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  const getLabelTypeLabel = (type: LabelType): string => {
    switch (type) {
      case 'POD': return t('production.templateSelector.labelTypePrintOnDemand');
      case 'P&P': return t('production.templateSelector.labelTypePickAndPack');
      case 'BOTH': return t('production.templateSelector.labelTypeUniversal');
      default: return type;
    }
  };

  const getPresetName = (key: string, size: { name: string }): string => {
    const presetKeys: Record<string, string> = {
      'compact-32x57': 'production.templateSelector.presetCompact32x57',
      'standard-4x3': 'production.templateSelector.presetStandard4x3',
      'shipping-4x6': 'production.templateSelector.presetShipping4x6',
    };
    return presetKeys[key] ? t(presetKeys[key]) : size.name;
  };

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newLabelType, setNewLabelType] = useState<LabelType>('BOTH');
  const [filterLabelType, setFilterLabelType] = useState<LabelType | 'ALL'>('ALL');
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const templateNameInputRef = useRef<HTMLInputElement>(null);

  // AlertDialog state for confirmations
  const [pendingLoadTemplate, setPendingLoadTemplate] = useState<NonNullable<typeof templates>[0] | null>(null);
  const [pendingDeleteTemplate, setPendingDeleteTemplate] = useState<{ id: string; name: string } | null>(null);
  const [pendingCanvasChange, setPendingCanvasChange] = useState<string | null>(null);

  // Auto-focus template name input when save dialog opens
  useEffect(() => {
    if (!saveDialogOpen || !templateNameInputRef.current) {
      return;
    }
    // Small delay to ensure dialog is fully rendered
    const timer = setTimeout(() => {
      templateNameInputRef.current?.focus();
      templateNameInputRef.current?.select();
    }, 50);
    return () => clearTimeout(timer);
  }, [saveDialogOpen]);

  const templateId = useLabelLayoutStore((s) => s.templateId);
  const templateName = useLabelLayoutStore((s) => s.templateName);
  const canvasSize = useLabelLayoutStore((s) => s.canvasSize);
  const components = useLabelLayoutStore((s) => s.components);
  const labelType = useLabelLayoutStore((s) => s.labelType);
  const isDirty = useLabelLayoutStore((s) => s.isDirty);
  const isBuiltIn = useLabelLayoutStore((s) => s.isBuiltIn);
  const setTemplate = useLabelLayoutStore((s) => s.setTemplate);
  const setCanvasSize = useLabelLayoutStore((s) => s.setCanvasSize);
  const setLabelType = useLabelLayoutStore((s) => s.setLabelType);
  const markClean = useLabelLayoutStore((s) => s.markClean);

  // Fetch templates and custom sizes for current org
  const { data: templates, refetch } = trpc.labelTemplates.list.useQuery();
  const { data: customSizesData } = trpc.productionSettings.getCustomLabelSizes.useQuery();
  const customSizes = useMemo(() => customSizesData?.customSizes ?? [], [customSizesData]);
  const createMutation = trpc.labelTemplates.create.useMutation();
  const updateMutation = trpc.labelTemplates.update.useMutation();
  const deleteMutation = trpc.labelTemplates.delete.useMutation();
  const setDefaultMutation = trpc.labelTemplates.setDefault.useMutation();
  const seedDefaultsMutation = trpc.labelTemplates.seedDefaults.useMutation();

  // Filter templates by label type
  const filteredTemplates = templates?.filter((tmpl) => {
    if (filterLabelType === 'ALL') return true;
    return tmpl.labelType === filterLabelType || tmpl.labelType === 'BOTH';
  });

  // Group filtered templates by canvas size
  const groupedTemplates = useMemo(() => {
    const groups = new Map<string, NonNullable<typeof filteredTemplates>>();
    for (const tmpl of filteredTemplates || []) {
      const key = tmpl.canvasSize.name || `${tmpl.canvasSize.width}×${tmpl.canvasSize.height}mm`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(tmpl);
    }
    return groups;
  }, [filteredTemplates]);

  const loadTemplate = useCallback(
    async (template: NonNullable<typeof templates>[0]): Promise<void> => {
      setIsLoadingTemplate(true);
      // Small delay to show loading state (template loading is synchronous but gives visual feedback)
      await new Promise(resolve => setTimeout(resolve, 150));
      setTemplate({
        id: template.id,
        name: template.name,
        canvasSize: template.canvasSize,
        components: template.components,
        labelType: template.labelType,
        isDefault: template.isDefault,
        isBuiltIn: template.isBuiltIn,
      });
      setIsLoadingTemplate(false);
      setPopoverOpen(false);
    },
    [setTemplate]
  );

  const handleLoadTemplate = useCallback(
    (template: NonNullable<typeof templates>[0]): void => {
      if (isDirty) {
        setPendingLoadTemplate(template);
        return;
      }
      loadTemplate(template);
    },
    [isDirty, loadTemplate]
  );

  const handleSaveTemplate = useCallback(async () => {
    if (!newTemplateName.trim()) {
      toast({ title: t('production.templateSelector.error'), description: t('production.templateSelector.errorRequired'), variant: 'destructive' });
      return;
    }

    try {
      if (templateId && !isBuiltIn) {
        // Update existing (cannot update built-in templates)
        await updateMutation.mutateAsync({
          id: templateId,
          name: newTemplateName,
          canvasSize,
          components,
          labelType: newLabelType,
        });
        toast({ title: t('production.templateSelector.saved'), description: t('production.templateSelector.savedUpdated') });
      } else {
        // Create new
        const result = await createMutation.mutateAsync({
          name: newTemplateName,
          canvasSize,
          components,
          labelType: newLabelType,
        });
        setTemplate({
          id: result.id,
          name: result.name,
          canvasSize: result.canvasSize,
          components: result.components,
          labelType: result.labelType,
          isDefault: result.isDefault,
          isBuiltIn: result.isBuiltIn,
        });
        toast({ title: t('production.templateSelector.saved'), description: t('production.templateSelector.savedCreated') });
      }
      markClean();
      refetch();
      setSaveDialogOpen(false);
    } catch (_error) {
      toast({ title: t('production.templateSelector.error'), description: t('production.templateSelector.failedSave'), variant: 'destructive' });
    }
  }, [
    newTemplateName,
    templateId,
    isBuiltIn,
    components,
    canvasSize,
    newLabelType,
    updateMutation,
    createMutation,
    setTemplate,
    markClean,
    refetch,
    toast,
    t,
  ]);

  const handleDuplicateTemplate = useCallback(
    async (id: string, name: string) => {
      // Find the template to duplicate
      const template = templates?.find((tmpl) => tmpl.id === id);
      if (!template) return;

      try {
        const result = await createMutation.mutateAsync({
          name: t('production.templateSelector.copyOf', { name }),
          canvasSize: template.canvasSize,
          components: template.components,
          labelType: template.labelType,
        });
        toast({ title: t('production.templateSelector.duplicated'), description: t('production.templateSelector.duplicatedDesc', { name: result.name }) });
        refetch();
      } catch (_error) {
        toast({ title: t('production.templateSelector.error'), description: t('production.templateSelector.failedDuplicate'), variant: 'destructive' });
      }
    },
    [templates, createMutation, refetch, toast, t]
  );

  const handleDeleteTemplate = useCallback(
    async (id: string) => {
      try {
        await deleteMutation.mutateAsync({ id });
        toast({ title: t('production.templateSelector.deleted'), description: t('production.templateSelector.deletedDesc') });
        refetch();
      } catch (error) {
        const message = error instanceof Error ? error.message : t('production.templateSelector.failedDelete');
        toast({ title: t('production.templateSelector.error'), description: message, variant: 'destructive' });
      }
    },
    [deleteMutation, refetch, toast, t]
  );

  const handleSetDefault = useCallback(
    async (id: string) => {
      try {
        await setDefaultMutation.mutateAsync({ id });
        toast({ title: t('production.templateSelector.updated'), description: t('production.templateSelector.updatedDesc') });
        refetch();
      } catch (_error) {
        toast({ title: t('production.templateSelector.error'), description: t('production.templateSelector.failedSetDefault'), variant: 'destructive' });
      }
    },
    [setDefaultMutation, refetch, toast, t]
  );

  const handleRestoreDefaults = useCallback(async () => {
    try {
      const seeded = await seedDefaultsMutation.mutateAsync();
      if (seeded.length === 0) {
        toast({ title: t('production.templateSelector.noChanges'), description: t('production.templateSelector.allDefaultsExist') });
      } else {
        toast({ title: t('production.templateSelector.restored'), description: t('production.templateSelector.restoredDesc', { count: seeded.length }) });
      }
      refetch();
    } catch (_error) {
      toast({ title: t('production.templateSelector.error'), description: t('production.templateSelector.failedRestoreDefaults'), variant: 'destructive' });
    }
  }, [seedDefaultsMutation, refetch, toast, t]);

  const applyCanvasChange = useCallback(
    (key: string) => {
      if (key in CANVAS_PRESETS) {
        setCanvasSize(CANVAS_PRESETS[key as CanvasPresetKey]);
      } else if (key.startsWith('custom:')) {
        const name = key.slice(7);
        const custom = customSizes.find(c => c.name === name);
        if (custom) setCanvasSize(custom);
      }
    },
    [setCanvasSize, customSizes]
  );

  const handleCanvasSizeChange = useCallback(
    (key: string) => {
      if (isDirty) {
        setPendingCanvasChange(key);
        return;
      }
      applyCanvasChange(key);
    },
    [isDirty, applyCanvasChange]
  );

  const handleLabelTypeChange = useCallback(
    (type: LabelType) => {
      setLabelType(type);
      // Also update filter to show relevant templates
      setFilterLabelType(type);
    },
    [setLabelType]
  );

  // Find which size key matches current canvas size (preset or custom)
  const currentSizeKey = useMemo(() => {
    const presetKey = Object.keys(CANVAS_PRESETS).find(
      (k) =>
        CANVAS_PRESETS[k as CanvasPresetKey].width === canvasSize.width &&
        CANVAS_PRESETS[k as CanvasPresetKey].height === canvasSize.height
    );
    if (presetKey) return presetKey;
    const customMatch = customSizes.find(
      c => c.width === canvasSize.width && c.height === canvasSize.height
    );
    if (customMatch) return `custom:${customMatch.name}`;
    return 'compact-32x57';
  }, [canvasSize.width, canvasSize.height, customSizes]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        {/* Settings row - collapses to single dropdown on mobile */}
        <div className="hidden sm:flex items-center gap-3">
          {/* Label Type Selector */}
          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">{t('production.templateSelector.labelType')}</Label>
            <Select value={labelType} onValueChange={(v) => handleLabelTypeChange(v as LabelType)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LABEL_TYPES.map((key) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className={LABEL_TYPE_COLORS[key]}>
                        {key}
                      </Badge>
                      <span className="text-xs text-muted-foreground hidden md:inline">{getLabelTypeLabel(key)}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Canvas Size Selector */}
          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">{t('production.templateSelector.size')}</Label>
            <Select
              value={currentSizeKey}
              onValueChange={(v) => handleCanvasSizeChange(v)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CANVAS_PRESETS).map(([key, size]) => (
                  <SelectItem key={key} value={key}>
                    {getPresetName(key, size)}
                  </SelectItem>
                ))}
                {customSizes.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase">{t('production.templateSelector.customSizeGroup')}</div>
                    {customSizes.map((size) => (
                      <SelectItem key={`custom:${size.name}`} value={`custom:${size.name}`}>
                        {size.name}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Mobile compact view - combined dropdown */}
        <div className="sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Badge variant="secondary" className={cn('text-[10px]', LABEL_TYPE_COLORS[labelType])}>
                  {labelType}
                </Badge>
                <span className="text-muted-foreground">|</span>
                <span className="text-xs">
                  {currentSizeKey in CANVAS_PRESETS
                    ? getPresetName(currentSizeKey, CANVAS_PRESETS[currentSizeKey as CanvasPresetKey])
                    : customSizes.find(c => `custom:${c.name}` === currentSizeKey)?.name ?? t('production.templateSelector.customSizeGroup')}
                </span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[280px]">
              <DropdownMenuLabel>{t('production.templateSelector.labelTypeLabel')}</DropdownMenuLabel>
              {LABEL_TYPES.map((key) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => handleLabelTypeChange(key)}
                  className="flex items-center gap-2"
                >
                  {labelType === key && <Check className="h-4 w-4" />}
                  <Badge variant="secondary" className={LABEL_TYPE_COLORS[key]}>
                    {key}
                  </Badge>
                  <span className="text-sm">{getLabelTypeLabel(key)}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t('production.templateSelector.canvasSizeLabel')}</DropdownMenuLabel>
              {Object.entries(CANVAS_PRESETS).map(([key, size]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => handleCanvasSizeChange(key)}
                  className="flex items-center gap-2"
                >
                  {currentSizeKey === key && <Check className="h-4 w-4" />}
                  <span className="text-sm">{getPresetName(key, size)}</span>
                </DropdownMenuItem>
              ))}
              {customSizes.map((size) => {
                const key = `custom:${size.name}`;
                return (
                  <DropdownMenuItem
                    key={key}
                    onClick={() => handleCanvasSizeChange(key)}
                    className="flex items-center gap-2"
                  >
                    {currentSizeKey === key && <Check className="h-4 w-4" />}
                    <span className="text-sm">{size.name}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Template Popover */}
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="min-w-[120px] sm:min-w-[180px] justify-between" disabled={isLoadingTemplate}>
              <span className="truncate flex items-center gap-2">
                {isLoadingTemplate ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <span className="truncate">{templateName}</span>
                )}
                {isDirty && !isLoadingTemplate && (
                  <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
                )}
              </span>
              <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[420px] p-0">
            {/* Header with filter */}
            <div className="px-3 py-2.5 border-b flex items-center justify-between">
              <span className="text-sm font-medium">
                {t('production.templateSelector.templates', { count: filteredTemplates?.length || 0 })}
              </span>
              <div className="flex items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <Select
                  value={filterLabelType}
                  onValueChange={(v) => setFilterLabelType(v as LabelType | 'ALL')}
                >
                  <SelectTrigger className="h-7 w-[110px] text-xs">
                    <SelectValue placeholder={t('production.templateSelector.filterPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{t('production.templateSelector.allTypes')}</SelectItem>
                    <SelectItem value="POD">{t('production.templateSelector.podOnly')}</SelectItem>
                    <SelectItem value="P&P">{t('production.templateSelector.ppOnly')}</SelectItem>
                    <SelectItem value="BOTH">{t('production.templateSelector.universal')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Template list */}
            <ScrollArea className="max-h-[400px]">
              {/* Empty state */}
              {filteredTemplates?.length === 0 && (
                <div className="px-3 py-6 text-center">
                  <p className="text-sm text-muted-foreground mb-2">{t('production.templateSelector.noTemplates')}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilterLabelType('ALL')}
                    className="text-xs"
                  >
                    {t('production.templateSelector.clearFilter')}
                  </Button>
                </div>
              )}

              {/* Grouped template list */}
              {Array.from(groupedTemplates.entries()).map(([groupName, groupTemplates], groupIndex) => (
                <div key={groupName}>
                  {/* Group header */}
                  {groupedTemplates.size > 1 && (
                    <div className={cn(
                      'px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50 uppercase tracking-wide',
                      groupIndex > 0 && 'border-t'
                    )}>
                      {groupName}
                    </div>
                  )}

                  {/* Templates in group */}
                  {groupTemplates.map((tmpl) => (
                    <div
                      key={tmpl.id}
                      className={cn(
                        'px-3 py-2 hover:bg-accent cursor-pointer border-b last:border-b-0',
                        tmpl.id === templateId && 'bg-accent/50'
                      )}
                      onClick={() => handleLoadTemplate(tmpl)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        {/* Template info - left side */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            {tmpl.id === templateId && (
                              <Check className="h-4 w-4 shrink-0 text-primary" />
                            )}
                            <span className="text-sm font-medium truncate">{tmpl.name}</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <Badge
                              variant="secondary"
                              className={cn('text-[10px] px-1.5 py-0', LABEL_TYPE_COLORS[tmpl.labelType])}
                            >
                              {tmpl.labelType}
                            </Badge>
                            {tmpl.isBuiltIn && (
                              <span className="text-[10px] text-muted-foreground">{t('production.templateSelector.builtIn')}</span>
                            )}
                            {tmpl.isDefault && (
                              <span className="text-[10px] text-amber-600 flex items-center gap-0.5">
                                <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />
                                {t('production.templateSelector.default')}
                              </span>
                            )}
                            {/* Show canvas size when there's only one group */}
                            {groupedTemplates.size <= 1 && (
                              <span className="text-[10px] text-muted-foreground">
                                {tmpl.canvasSize.width}×{tmpl.canvasSize.height}mm
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Action buttons - right side */}
                        <div className="flex items-center gap-0.5 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateTemplate(tmpl.id, tmpl.name);
                            }}
                            disabled={createMutation.isPending}
                            aria-label={t('production.templateSelector.duplicateAria', { name: tmpl.name })}
                          >
                            {createMutation.isPending ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetDefault(tmpl.id);
                            }}
                            disabled={setDefaultMutation.isPending || tmpl.isDefault}
                            aria-label={tmpl.isDefault ? t('production.templateSelector.alreadyDefaultAria', { name: tmpl.name }) : t('production.templateSelector.setDefaultAria', { name: tmpl.name })}
                          >
                            {setDefaultMutation.isPending ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Star className={cn(
                                'h-3.5 w-3.5',
                                tmpl.isDefault && 'fill-amber-500 text-amber-500'
                              )} />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingDeleteTemplate({ id: tmpl.id, name: tmpl.name });
                            }}
                            disabled={deleteMutation.isPending}
                            aria-label={t('production.templateSelector.deleteAria', { name: tmpl.name })}
                          >
                            {deleteMutation.isPending ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-destructive" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </ScrollArea>

            {/* Footer */}
            <div className="px-3 py-2 border-t flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs gap-1.5 text-muted-foreground"
                onClick={() => {
                  handleRestoreDefaults();
                }}
                disabled={seedDefaultsMutation.isPending}
              >
                {seedDefaultsMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5" />
                )}
                {t('production.templateSelector.restoreDefaults')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs gap-1.5"
                onClick={() => {
                  setNewTemplateName(templateId ? t('production.templateSelector.copyOf', { name: templateName }) : '');
                  setNewLabelType(labelType);
                  setSaveDialogOpen(true);
                  setPopoverOpen(false);
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                {t('production.templateSelector.saveAsNew')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Save Button */}
        <Button
          onClick={() => {
            setNewTemplateName(templateName);
            setNewLabelType(labelType);
            setSaveDialogOpen(true);
          }}
          disabled={!isDirty || updateMutation.isPending || createMutation.isPending}
          size="sm"
        >
          {(updateMutation.isPending || createMutation.isPending) ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          {t('production.templateSelector.save')}
        </Button>
      </div>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('production.templateSelector.saveTemplate')}</DialogTitle>
            <DialogDescription>
              {t('production.templateSelector.saveTemplateDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">{t('production.templateSelector.templateName')}</Label>
              <Input
                ref={templateNameInputRef}
                id="template-name"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder={t('production.templateSelector.namePlaceholder')}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label-type">{t('production.templateSelector.labelTypeLabel')}</Label>
              <Select value={newLabelType} onValueChange={(v) => setNewLabelType(v as LabelType)}>
                <SelectTrigger id="label-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LABEL_TYPES.map((key) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={LABEL_TYPE_COLORS[key]}>
                          {key}
                        </Badge>
                        <span>{getLabelTypeLabel(key)}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('production.templateSelector.podDescription')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              {t('production.common.cancel')}
            </Button>
            <Button onClick={handleSaveTemplate} disabled={!newTemplateName.trim()}>
              {t('production.templateSelector.saveTemplate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog: Load template with unsaved changes */}
      <AlertDialog
        open={!!pendingLoadTemplate}
        onOpenChange={(open) => { if (!open) setPendingLoadTemplate(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('production.templateSelector.unsavedChanges')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('production.templateSelector.unsavedChangesDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('production.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (pendingLoadTemplate) loadTemplate(pendingLoadTemplate);
              setPendingLoadTemplate(null);
            }}>
              {t('production.templateSelector.discardAndLoad')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog: Delete template confirmation */}
      <AlertDialog
        open={!!pendingDeleteTemplate}
        onOpenChange={(open) => { if (!open) setPendingDeleteTemplate(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('production.templateSelector.deleteTemplate')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('production.templateSelector.deleteTemplateDesc', { name: pendingDeleteTemplate?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('production.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingDeleteTemplate) handleDeleteTemplate(pendingDeleteTemplate.id);
                setPendingDeleteTemplate(null);
              }}
            >
              {t('production.common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog: Canvas size change with unsaved changes */}
      <AlertDialog
        open={!!pendingCanvasChange}
        onOpenChange={(open) => { if (!open) setPendingCanvasChange(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('production.templateSelector.changeCanvasSize')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('production.templateSelector.changeCanvasSizeDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('production.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (pendingCanvasChange) applyCanvasChange(pendingCanvasChange);
              setPendingCanvasChange(null);
            }}>
              {t('production.templateSelector.changeSize')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
